<template>
  <Wrapper>
    <Dashboard>
      <div class="pa-4">
        <v-card
          class="rounded-lg pa-4 w-100 mx-auto"
          max-width="600"
          variant="flat"
        >
          <h1 class="text-headline-small mb-4">
            {{ t('dashboard.signed_with_deep_id.title') }}
          </h1>

          <SignatureHistoryList v-if="entries.length" :entries="entries" />

          <p
            v-else-if="!isLoading && !hasError"
            class="text-body-medium text-medium-emphasis"
          >
            {{ t('dashboard.signed_with_deep_id.empty') }}
          </p>

          <div v-if="isLoading" class="d-flex justify-center my-4">
            <DeepLoaderAnimation />
          </div>

          <div
            v-else-if="hasError"
            class="d-flex flex-column align-center ga-2 my-4"
          >
            <p class="text-body-medium text-medium-emphasis">
              {{ t('dashboard.signed_with_deep_id.load_error') }}
            </p>
            <VBtnSecondary @click="loadMore">
              {{ t('actions.retry') }}
            </VBtnSecondary>
          </div>

          <div v-else-if="hasMore" class="d-flex justify-center mt-4">
            <VBtnSecondary @click="loadMore">
              {{ t('dashboard.signed_with_deep_id.load_more') }}
            </VBtnSecondary>
          </div>
        </v-card>
      </div>
    </Dashboard>
  </Wrapper>
</template>

<script setup lang="ts">
import { DeepLoaderAnimation } from '@deepcloud/deep-ui-lib'
import {
  mapToHistoryEntry,
  SignatureHistoryEntry,
  sortHistoryEntries,
} from '@/composables/sign/use-signature-history.ts'
import { useApi } from '@/composables/use-api.ts'
import { useUserSession } from '@/composables/use-user-session.ts'
import SignatureHistoryList from '@/components/dashboard/SignatureHistoryList.vue'
import Dashboard from '@/components/wrappers/Dashboard.vue'
import Wrapper from '@/components/wrappers/Wrapper.vue'

definePage({
  name: 'sign-history',
  meta: {
    titleKey: 'tab.sign_history',
    requiresAuth: true,
  },
})

// The dashboard preview reads from the cached, unpaginated session data,
// which is fine for a truncated top-3 list - but "show all" only means
// something if this page actually fetches everything, page by page.
const PAGE_SIZE = 20

const { t } = useI18n()
const { session } = useUserSession()
const { deepIdAPIV1 } = useApi()

const entries = ref<SignatureHistoryEntry[]>([])
const isLoading = ref(false)
const hasError = ref(false)
const hasMore = ref(true)
let offset = 0

async function loadMore() {
  const personUid = session.value.personUid
  if (!personUid || isLoading.value || !hasMore.value) return

  isLoading.value = true
  hasError.value = false

  try {
    const page = await deepIdAPIV1.value.naturalPersonsSignatureRequests
      .getById(personUid, { offset: String(offset), count: String(PAGE_SIZE) })
      .then((response) => response.data)

    offset += page.length
    hasMore.value = page.length === PAGE_SIZE
    entries.value = sortHistoryEntries([
      ...entries.value,
      ...page
        .map(mapToHistoryEntry)
        .filter((entry): entry is SignatureHistoryEntry => !!entry),
    ])
  } catch {
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}

onMounted(loadMore)
</script>
